import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  DateField,
  IconButton,
  MonoLabel,
  PickOrCreate,
  Popover,
  Tag,
  TextField,
  UserPicker,
  type PickOrCreateOption,
  type UserPickerOption
} from '../primitives';
import type {
  AssigneeOption,
  CustomAttributeDefRow,
  CustomAttributeOptionRow,
  CustomAttributeValueRow,
  CustomAttributeValueVersionRow
} from '../../lib/vm/useDocumentEditorVM';

/**
 * Inspector custom-attribute row. Turn 14.3b shipped the four typed kinds —
 * `text` / `number` / `url` / `date` — all using the existing `<TextField>`
 * primitive with `type` mapped from `def.kind`. Turn 14.3c wires the
 * `user`-kind control through the new `<UserPicker>` typeahead primitive.
 * Future turns add `single_select` (14.3d), `multi_select` (14.3e), and the
 * archived-attribute group (14.3f).
 *
 * Commit cadence:
 *   - Typing schedules a 600ms debounce commit.
 *   - `onBlur` flushes the pending debounce immediately.
 *   - Clearing the input writes `null` (UPSERT with `value = null`).
 *
 * Disabled in read-only mode (`!editable`).
 */

export interface AttributeRowProps {
  def: CustomAttributeDefRow;
  value: CustomAttributeValueRow | undefined;
  editable: boolean;
  onCommit: (attrDefId: string, value: unknown) => void | Promise<unknown>;
  /** Active org members — used by the `user`-kind picker (14.3c). Threaded
   *  by the parent so kinds that need a member list don't re-fetch. */
  userOptions?: AssigneeOption[];
  /** Active option pool for this def — used by the `single_select` picker
   *  (14.3d) + `multi_select` (14.3e). Empty / undefined ⇒ pick list is
   *  empty but the "+ Create" footer still works against typed input. */
  selectOptions?: CustomAttributeOptionRow[];
  /** Mint a new option for this def via `create_custom_attribute_option`.
   *  Required when `def.kind === 'single_select' | 'multi_select'`. */
  onCreateOption?: (
    attrDefId: string,
    label: string
  ) => Promise<CustomAttributeOptionRow | null>;
  /** Archived defs render under a muted bottom group with disabled value
   *  controls + a clock popover for per-row restore. */
  archived?: boolean;
  /** Loads `custom_attribute_value_versions` for the row; required when
   *  `archived` so the popover can list history. */
  onFetchVersions?: (
    attrDefId: string
  ) => Promise<CustomAttributeValueVersionRow[]>;
  /** `compact` hides the row label — used inside property-bar popovers. */
  layout?: 'default' | 'compact';
  /** Focus the primary control when mounted (property-bar typed fields). */
  autoFocusControl?: boolean;
}

const TYPED_KINDS = new Set(['text', 'number', 'url', 'date']);

function readUserValue(
  v: CustomAttributeValueRow['value'] | undefined
): { user_id: string } | null {
  if (v == null) return null;
  if (typeof v === 'object' && !Array.isArray(v)) {
    const userId = (v as { user_id?: unknown }).user_id;
    if (typeof userId === 'string' && userId.length > 0) {
      return { user_id: userId };
    }
  }
  return null;
}

function readOptionValue(
  v: CustomAttributeValueRow['value'] | undefined
): { option_id: string } | null {
  if (v == null) return null;
  if (typeof v === 'object' && !Array.isArray(v)) {
    const optId = (v as { option_id?: unknown }).option_id;
    if (typeof optId === 'string' && optId.length > 0) {
      return { option_id: optId };
    }
  }
  return null;
}

function readOptionIds(
  v: CustomAttributeValueRow['value'] | undefined
): string[] {
  if (v == null) return [];
  if (typeof v === 'object' && !Array.isArray(v)) {
    const ids = (v as { option_ids?: unknown }).option_ids;
    if (Array.isArray(ids)) {
      return ids.filter(
        (id): id is string => typeof id === 'string' && id.length > 0
      );
    }
  }
  return [];
}

const COMMIT_DEBOUNCE_MS = 600;

function AttributeRowFrame({
  def,
  layout,
  children
}: {
  def: CustomAttributeDefRow;
  layout: 'default' | 'compact';
  children: ReactNode;
}) {
  const rowTestId = `document-editor-attr-${def.key}`;
  if (layout === 'compact') {
    return (
      <div
        className="de-row de-row-compact"
        data-testid={rowTestId}
        data-attr-kind={def.kind}
      >
        {children}
      </div>
    );
  }
  return (
    <div
      className="de-row"
      data-testid={rowTestId}
      data-attr-kind={def.kind}
    >
      <MonoLabel size="sm" tracking="wider" className="de-row-label">
        {def.label}
      </MonoLabel>
      <div className="de-row-value">{children}</div>
    </div>
  );
}

function readScalar(v: CustomAttributeValueRow['value'] | undefined): string {
  if (v == null) return '';
  // Stored as a JSON scalar — text/url → string, number → number, date → ISO string.
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return '';
}

export function AttributeRow({
  def,
  value,
  editable,
  onCommit,
  userOptions,
  selectOptions,
  onCreateOption,
  archived,
  onFetchVersions,
  layout = 'default',
  autoFocusControl = false
}: AttributeRowProps) {
  // Initialise the local draft off the persisted value. The picker re-syncs
  // when the underlying value object swaps (e.g. external save). Hooks run
  // unconditionally so typed rows and early-return kinds share one call order.
  const persisted = readScalar(value?.value);
  const [draft, setDraft] = useState<string>(persisted);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftRef = useRef<string>(persisted);

  // Re-sync local draft when the persisted server value changes (e.g. another
  // tab saved). We do NOT clobber an in-flight edit — only reset when the
  // input isn't focused (the user isn't typing into it).
  useEffect(() => {
    if (debounceRef.current) return;
    if (draftRef.current === persisted) return;
    setDraft(persisted);
    draftRef.current = persisted;
  }, [persisted]);

  // Cleanup the pending timer if the row unmounts mid-debounce.
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, []);

  if (archived) {
    return (
      <ArchivedAttributeRow
        def={def}
        value={value}
        onCommit={onCommit}
        onFetchVersions={onFetchVersions}
        editable={editable}
      />
    );
  }

  if (def.kind === 'user') {
    const userValue = readUserValue(value?.value);
    const opts: UserPickerOption[] = (userOptions ?? []).map((u) => ({
      user_id: u.user_id,
      display_name: u.display_name,
      username: u.username
    }));
    return (
      <AttributeRowFrame def={def} layout={layout}>
        <UserPicker
          id={`document-editor-attr-${def.key}-picker`}
          label={def.label}
          options={opts}
          value={userValue}
          disabled={!editable}
          onPick={(picked) => {
            const next = picked ? { user_id: picked.user_id } : null;
            void onCommit(def.id, next);
          }}
          data-testid={`document-editor-attr-${def.key}-picker`}
        />
      </AttributeRowFrame>
    );
  }

  if (def.kind === 'single_select') {
    const optValue = readOptionValue(value?.value);
    const opts: PickOrCreateOption[] = (selectOptions ?? []).map((o) => ({
      id: o.id,
      label: o.label
    }));
    return (
      <AttributeRowFrame def={def} layout={layout}>
        <PickOrCreate
          id={`document-editor-attr-${def.key}-picker`}
          label={def.label}
          options={opts}
          value={optValue}
          disabled={!editable}
          onPick={(picked) => {
            const next = picked ? { option_id: picked.id } : null;
            void onCommit(def.id, next);
          }}
          onCreate={async (label) => {
            if (!onCreateOption) return null;
            const row = await onCreateOption(def.id, label);
            if (!row) return null;
            return { id: row.id, label: row.label };
          }}
          data-testid={`document-editor-attr-${def.key}-picker`}
        />
      </AttributeRowFrame>
    );
  }

  if (def.kind === 'multi_select') {
    const optionIds = readOptionIds(value?.value);
    const allOptions = selectOptions ?? [];
    const byId = new Map(allOptions.map((o) => [o.id, o] as const));
    const remaining: PickOrCreateOption[] = allOptions
      .filter((o) => !optionIds.includes(o.id))
      .map((o) => ({ id: o.id, label: o.label }));
    const commitNext = (nextIds: string[]) => {
      const next = nextIds.length === 0 ? null : { option_ids: nextIds };
      void onCommit(def.id, next);
    };
    return (
      <AttributeRowFrame def={def} layout={layout}>
        <div className="de-row-value de-row-multi">
          <div
            className="de-multi-strip"
            data-testid={`document-editor-attr-${def.key}-strip`}
          >
            {optionIds.map((id) => {
              const opt = byId.get(id);
              const label = opt?.label ?? '(missing)';
              return (
                <Tag
                  key={id}
                  dismissible
                  disabled={!editable}
                  dismissLabel={`Remove ${label}`}
                  onDismiss={() => {
                    commitNext(optionIds.filter((x) => x !== id));
                  }}
                  data-testid={`document-editor-attr-${def.key}-tag-${id}`}
                >
                  {label}
                </Tag>
              );
            })}
          </div>
          <PickOrCreate
            id={`document-editor-attr-${def.key}-picker`}
            label={def.label}
            options={remaining}
            value={null}
            disabled={!editable}
            placeholder="+ add"
            onPick={(picked) => {
              if (!picked) return;
              if (optionIds.includes(picked.id)) return;
              commitNext([...optionIds, picked.id]);
            }}
            onCreate={async (label) => {
              if (!onCreateOption) return null;
              const row = await onCreateOption(def.id, label);
              if (!row) return null;
              return { id: row.id, label: row.label };
            }}
            data-testid={`document-editor-attr-${def.key}-picker`}
          />
        </div>
      </AttributeRowFrame>
    );
  }

  if (!TYPED_KINDS.has(def.kind)) {
    // Unknown kind — render a muted placeholder so the row still appears.
    return (
      <div
        className="de-row"
        data-testid={`document-editor-attr-${def.key}`}
        data-attr-kind={def.kind}
      >
        <MonoLabel size="sm" tracking="wider" className="de-row-label">
          {def.label}
        </MonoLabel>
        <div className="de-row-value de-row-attr-placeholder">—</div>
      </div>
    );
  }

  // text → text, number → number, url → url, date → date (1:1 map).
  const inputType = def.kind;

  const coerce = (raw: string): unknown => {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    if (def.kind === 'number') {
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : null;
    }
    return trimmed;
  };

  const flush = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const next = coerce(draftRef.current);
    const current = value?.value ?? null;
    if (next === current) return;
    void onCommit(def.id, next);
  };

  const scheduleCommit = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      flush();
    }, COMMIT_DEBOUNCE_MS);
  };

  const onChange = (next: string) => {
    setDraft(next);
    draftRef.current = next;
    scheduleCommit();
  };

  const onBlur = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
      flush();
    }
  };

  const inputId = `document-editor-attr-${def.key}-input`;

  if (def.kind === 'date') {
    const dateValue =
      persisted.trim() === '' ? null : persisted.trim().slice(0, 10);
    return (
      <AttributeRowFrame def={def} layout={layout}>
        <DateField
          id={inputId}
          label={def.label}
          hideLabel
          value={dateValue}
          disabled={!editable}
          testId={`document-editor-attr-${def.key}-input`}
          className="de-attr-input"
          autoFocus={autoFocusControl}
          onChange={(next) => {
            setDraft(next ?? '');
            draftRef.current = next ?? '';
            void onCommit(def.id, next);
          }}
        />
      </AttributeRowFrame>
    );
  }

  return (
    <AttributeRowFrame def={def} layout={layout}>
      <TextField
        id={inputId}
        label={def.label}
        hideLabel
        type={inputType}
        value={draft}
        disabled={!editable}
        autoFocus={autoFocusControl}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        data-testid={`document-editor-attr-${def.key}-input`}
        className="de-attr-input"
      />
    </AttributeRowFrame>
  );
}

function renderArchivedDisplay(
  value: CustomAttributeValueRow | undefined
): string {
  const v = value?.value;
  if (v == null) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

interface ArchivedAttributeRowProps {
  def: CustomAttributeDefRow;
  value: CustomAttributeValueRow | undefined;
  editable: boolean;
  onCommit: (attrDefId: string, value: unknown) => void | Promise<unknown>;
  onFetchVersions?: (
    attrDefId: string
  ) => Promise<CustomAttributeValueVersionRow[]>;
}

function ArchivedAttributeRow({
  def,
  value,
  editable,
  onCommit,
  onFetchVersions
}: ArchivedAttributeRowProps) {
  const [versions, setVersions] = useState<
    CustomAttributeValueVersionRow[] | null
  >(null);
  const [loadingVersions, setLoadingVersions] = useState(false);

  const display = renderArchivedDisplay(value);

  const loadVersions = async () => {
    if (versions != null || loadingVersions || !onFetchVersions) return;
    setLoadingVersions(true);
    const rows = await onFetchVersions(def.id);
    setVersions(rows);
    setLoadingVersions(false);
  };

  return (
    <div
      className="de-row de-row-archived"
      data-testid={`document-editor-attr-${def.key}`}
      data-attr-kind={def.kind}
      data-archived="1"
    >
      <MonoLabel size="sm" tracking="wider" className="de-row-label">
        {def.label}
      </MonoLabel>
      <div className="de-row-value de-row-archived-value">
        <span
          className="de-row-archived-display"
          data-testid={`document-editor-attr-${def.key}-display`}
        >
          {display}
        </span>
        <Popover
          label={`${def.label} history`}
          align="end"
          data-testid={`document-editor-attr-${def.key}-popover`}
          trigger={({ onClick, ...api }) => (
            <IconButton
              aria-label="Attribute history"
              data-testid={`document-editor-attr-${def.key}-clock`}
              onClick={() => {
                void loadVersions();
                onClick();
              }}
              {...api}
            >
              ⌚
            </IconButton>
          )}
        >
          {({ close }) => (
            <div
              className="de-attr-history"
              data-testid={`document-editor-attr-${def.key}-history`}
            >
              {loadingVersions ? (
                <div className="de-attr-history-empty">Loading…</div>
              ) : versions && versions.length > 0 ? (
                versions.map((v) => (
                  <div
                    key={v.id}
                    className="de-attr-history-row"
                    data-testid={`document-editor-attr-${def.key}-history-row-${v.version_number}`}
                  >
                    <div className="de-attr-history-meta">
                      <MonoLabel size="sm" tracking="wider">
                        v{v.version_number}
                      </MonoLabel>
                      <span className="de-attr-history-time">
                        {new Date(v.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="de-attr-history-value">
                      {renderArchivedDisplay({
                        ...(value ?? {
                          id: '',
                          attr_def_id: def.id,
                          document_id: '',
                          org_id: '',
                          value: null,
                          created_at: '',
                          created_by: null,
                          updated_at: '',
                          updated_by: null
                        }),
                        value: v.value
                      } as CustomAttributeValueRow)}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!editable}
                      onClick={() => {
                        void onCommit(def.id, v.value);
                        close();
                      }}
                      data-testid={`document-editor-attr-${def.key}-history-restore-${v.version_number}`}
                    >
                      Restore
                    </Button>
                  </div>
                ))
              ) : (
                <div className="de-attr-history-empty">No history yet.</div>
              )}
            </div>
          )}
        </Popover>
      </div>
    </div>
  );
}
